/**
 * LocationSearch - Multi-mode location search component
 * Replaces ZipCodeSearch with three search modes:
 * - Studio Name: Google Places Autocomplete for establishments
 * - Address: Google Places Autocomplete for addresses
 * - ZIP Code: Plain 5-digit ZIP input (legacy behavior)
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlacesAutocomplete, type PlaceResult } from './PlacesAutocomplete';
import { useAuth } from '../../context/AuthContext';
import { getStudioProfile } from '../../api/studios';
import { isValidPostalCode } from '@sda/shared';
import { buildLocationParams } from '../../utils/location';

type SearchMode = 'studio' | 'address' | 'zip';

interface LocationSearchProps {
  /** Callback when search is submitted */
  onSearch?: (location: {
    zip?: string;
    lat?: number;
    lng?: number;
    displayLabel?: string;
  }) => void;
  /** Layout variant */
  variant?: 'hero' | 'compact';
  /** True while the parent page is fetching search results — keeps button disabled until results arrive */
  isSearching?: boolean;
  /**
   * When true (default), authenticated studio users get their ZIP pre-filled and
   * the search auto-fires on first load. Set false to opt out (e.g. the home hero,
   * where auto-firing would bounce a logged-in studio user straight to /events).
   */
  autoSearch?: boolean;
}

export function LocationSearch({
  onSearch,
  variant = 'hero',
  isSearching = false,
  autoSearch = true,
}: LocationSearchProps) {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  const [mode, setMode] = useState<SearchMode>('zip');
  const [zip, setZip] = useState('');
  const [zipError, setZipError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [studioDefaultAddress, setStudioDefaultAddress] = useState('');
  const [hasAutoSearched, setHasAutoSearched] = useState(false);

  // Button is busy if the local navigate/callback is in flight OR if the parent is fetching results
  const isButtonBusy = isLoading || isSearching;

  // Auto-populate studio address for authenticated studio users
  useEffect(() => {
    if (!autoSearch) return;
    if (!isAuthenticated || !user?.studio_id) return;

    // Don't override an explicit URL-provided location.
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('zip') || urlParams.has('lat') || urlParams.has('lng')) {
      setHasAutoSearched(true);
      return;
    }

    getStudioProfile()
      .then((studio) => {
        if (studio.zip_code) {
          // Build a display label from studio address
          const parts: string[] = [];
          if (studio.address_line1) parts.push(studio.address_line1);
          if (studio.city) parts.push(studio.city);
          if (studio.state) parts.push(studio.state);
          const label = parts.length > 0 ? parts.join(', ') : studio.zip_code;

          setStudioDefaultAddress(label);
          setZip(studio.zip_code);

          // Auto-trigger search with studio's zip on first load
          if (!hasAutoSearched) {
            setHasAutoSearched(true);
            const location = {
              zip: studio.zip_code,
              displayLabel: label,
            };
            if (onSearch) {
              onSearch(location);
            } else {
              navigate(`/events?zip=${encodeURIComponent(studio.zip_code)}`);
            }
          }
        }
      })
      .catch(() => {
        // Silently fail - user can still search manually
      });
  }, [isAuthenticated, user?.studio_id, autoSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlaceSelected = useCallback(
    (place: PlaceResult) => {
      const displayLabel =
        mode === 'studio'
          ? place.name || place.formattedAddress
          : place.formattedAddress;

      const location = {
        lat: place.lat,
        lng: place.lng,
        zip: place.addressComponents?.zip,
        displayLabel,
      };

      setIsLoading(true);
      try {
        if (onSearch) {
          onSearch(location);
        } else {
          // Navigate to search results page
          const qs = buildLocationParams({
            zip: place.addressComponents?.zip,
            lat: place.lat != null ? String(place.lat) : undefined,
            lng: place.lng != null ? String(place.lng) : undefined,
            q: displayLabel || undefined,
          });
          if (qs) navigate(`/events?${qs}`);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [mode, onSearch, navigate]
  );

  const validateZip = (value: string): boolean => {
    return isValidPostalCode(value);
  };

  const handleZipSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setZipError(null);

      const trimmedZip = zip.trim();

      if (!trimmedZip) {
        setZipError('Please enter your ZIP or postal code');
        return;
      }

      if (!validateZip(trimmedZip)) {
        setZipError('Please enter a valid US ZIP or Canadian postal code');
        return;
      }

      setIsLoading(true);
      try {
        if (onSearch) {
          onSearch({ zip: trimmedZip, displayLabel: trimmedZip });
        } else {
          navigate(`/events?zip=${encodeURIComponent(trimmedZip)}`);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [zip, onSearch, navigate]
  );

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow letters + digits + space (Canadian postal codes), uppercase, cap at 7 chars ("A1A 1A1")
    const value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '').toUpperCase().slice(0, 7);
    setZip(value);
    if (zipError) setZipError(null);
  };

  // Mode toggle strip
  const modeOptions: { key: SearchMode; label: string }[] = [
    { key: 'zip', label: 'ZIP / Postal' },
    { key: 'address', label: 'Address' },
    { key: 'studio', label: 'Studio/School Name' },
  ];

  const isHero = variant === 'hero';

  // Shared input class
  const inputClass = isHero
    ? 'input w-full text-lg py-3'
    : 'input w-full';

  const toggleStrip = (
    <div className="flex rounded-lg overflow-hidden border border-warm-300 mb-3">
      {modeOptions.map((opt) => (
        <button
          key={opt.key}
          type="button"
          onClick={() => {
            setMode(opt.key);
            setZipError(null);
          }}
          className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
            mode === opt.key
              ? 'bg-primary-600 text-white'
              : 'bg-warm-50 text-warm-600 hover:bg-warm-100'
          }`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  // Compact variant
  if (!isHero) {
    return (
      <div className="w-full">
        {toggleStrip}
        {mode === 'zip' ? (
          <form onSubmit={handleZipSubmit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={zip}
                onChange={handleZipChange}
                placeholder="ZIP / Postal code"
                className={`${inputClass} ${zipError ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                aria-label="ZIP or postal code"
                aria-invalid={!!zipError}
              />
              {zipError && (
                <p className="mt-1 text-danger-500 text-xs">{zipError}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isButtonBusy}
              className="btn-primary whitespace-nowrap"
            >
              {isButtonBusy ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                  Searching...
                </span>
              ) : (
                'Search'
              )}
            </button>
          </form>
        ) : (
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <PlacesAutocomplete
                mode={mode === 'studio' ? 'studio' : 'address'}
                onPlaceSelected={handlePlaceSelected}
                className={inputClass}
                defaultValue={mode === 'address' ? studioDefaultAddress : ''}
                placeholder={
                  mode === 'studio'
                    ? 'Search by studio name...'
                    : 'Enter an address...'
                }
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  // Hero variant
  return (
    <div className="w-full max-w-xl">
      {toggleStrip}
      <form onSubmit={mode === 'zip' ? handleZipSubmit : (e) => e.preventDefault()}>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              {mode === 'zip' ? (
                <svg
                  className="h-5 w-5 text-warm-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              ) : (
                <svg
                  className="h-5 w-5 text-warm-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              )}
            </div>
            {mode === 'zip' ? (
              <input
                type="text"
                value={zip}
                onChange={handleZipChange}
                placeholder="Enter your ZIP / postal code"
                className={`${inputClass} pl-12 ${
                  zipError ? 'border-danger-500 focus:ring-danger-500' : ''
                }`}
                aria-label="ZIP or postal code"
                aria-describedby={zipError ? 'zip-error' : undefined}
                aria-invalid={!!zipError}
              />
            ) : (
              <PlacesAutocomplete
                mode={mode === 'studio' ? 'studio' : 'address'}
                onPlaceSelected={handlePlaceSelected}
                className={`${inputClass} pl-12`}
                defaultValue={mode === 'address' ? studioDefaultAddress : ''}
                placeholder={
                  mode === 'studio'
                    ? 'Search by studio name...'
                    : 'Enter an address...'
                }
              />
            )}
          </div>
          <button
            type={mode === 'zip' ? 'submit' : 'button'}
            disabled={isButtonBusy}
            className="btn-primary text-lg py-3 px-8 whitespace-nowrap"
          >
            {isButtonBusy ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Searching...
              </span>
            ) : (
              'Find Classes Near Me'
            )}
          </button>
        </div>

        {zipError && (
          <p id="zip-error" className="mt-2 text-danger-300 text-sm" role="alert">
            {zipError}
          </p>
        )}
      </form>
    </div>
  );
}

export default LocationSearch;
